import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import {
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} from "./holidaysApi";

// Mirrors GUI/Master/frm_holidays.cs, now against the live
// /api/v1/masters/holidays endpoints (handoff §9.3). isSunday: 0 = holiday
// (specific date), 1 = Sunday (recurring, picked by month) - matches the
// legacy radio-button values, not a real boolean. Reading requires
// Holidays.View (route-gated); writes gated by Holidays.Create/.Edit + an
// all-branch caller (§13.3).
//
// Sunday rows follow the legacy convention (holidays_master since 2012, read by
// Texas_Admin holidays_master_BL.getSundays and sp_leave_get_month_calc): exactly
// ONE row per month with is_Sunday=1, whose Reason is the NUMBER of Sundays in
// that month as text ("4"/"5"). It is a count row, not a calendar of dates.
//
// History: a 2026-08-25 change made this page create one row per Sunday DATE
// with the typed reason - that broke the legacy payroll forms (Reason "SUNDAY"
// is not a number) and left 4-5 rows per month. September was cleaned up by
// hand in the data only, so October 2026 came out as 4 rows again (2026-09-27).
// Now: picking "Sundays" + a month saves ONE row dated the first real Sunday of
// the month, Reason = the Sunday count (computed, not typed), and refuses a
// second Sunday row for a month that already has one.
const sundaysInMonth = (monthStr) => {
  const start = dayjs(monthStr, "YYYY-MM").startOf("month");
  const sundays = [];
  for (let d = 0; d < start.daysInMonth(); d += 1) {
    const day = start.add(d, "day");
    if (day.day() === 0) sundays.push(day);
  }
  return sundays;
};
const isSundayRow = (record) => Number(record.isSunday) === 1;

const HolidaysPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Holidays");
  const { data: records = [], isLoading } = useGetHolidaysQuery();
  const [createHoliday, { isLoading: isCreating }] = useCreateHolidayMutation();
  const [updateHoliday, { isLoading: isUpdating }] = useUpdateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();

  const [mode, setMode] = useState("new");
  const [selectedId, setSelectedId] = useState(null);
  const [type, setType] = useState("holiday"); // "holiday" | "sunday"
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");

  const selectedRecord = records.find((r) => r.id === selectedId) || null;

  useEffect(() => {
    if (mode === "new") {
      setType("holiday");
      setDate("");
      setReason("");
    } else if (selectedRecord) {
      const isSunday = isSundayRow(selectedRecord);
      setType(isSunday ? "sunday" : "holiday");
      // A Sunday row stands for its whole month, so it is edited by month;
      // a holiday row is one specific date.
      setDate(dayjs(selectedRecord.date).format(isSunday ? "YYYY-MM" : "YYYY-MM-DD"));
      setReason(selectedRecord.reason || "");
    }
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;
  const isMonthPicker = type === "sunday";
  // The Sunday count is derived from the month, never typed - typing it is how
  // "4" became a row's reason four times over.
  const monthSundays = isMonthPicker && date ? sundaysInMonth(date) : [];
  const sundayCount = monthSundays.length;
  const existingSundayRowFor = (monthStr) =>
    records.find((r) => isSundayRow(r) && dayjs(r.date).format("YYYY-MM") === monthStr && r.id !== selectedId) || null;

  const select = (id) => {
    setSelectedId(id);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedId(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!date) {
      toast.error(isMonthPicker ? "Month is required" : "Date is required");
      return;
    }
    try {
      if (isMonthPicker) {
        const monthLabel = dayjs(date, "YYYY-MM").format("MMMM YYYY");
        if (sundayCount === 0) {
          toast.error("No Sundays found in that month");
          return;
        }
        // One count row per month - a second one would double-count in payroll.
        const duplicate = existingSundayRowFor(date);
        if (duplicate) {
          toast.error(`Sundays for ${monthLabel} are already entered (Id ${duplicate.id}) - edit that row instead`);
          return;
        }
        const body = {
          date: monthSundays[0].format("YYYY-MM-DDT00:00:00"),
          reason: String(sundayCount),
          isSunday: 1,
        };
        if (mode === "edit" && selectedId != null) {
          await updateHoliday({ id: selectedId, ...body }).unwrap();
        } else {
          await createHoliday(body).unwrap();
        }
        toast.success(`${monthLabel}: ${sundayCount} Sundays saved`);
      } else {
        const body = {
          date: dayjs(date, "YYYY-MM-DD").format("YYYY-MM-DDT00:00:00"),
          reason: reason.trim(),
          isSunday: type === "sunday" ? 1 : 0,
        };
        if (mode === "edit" && selectedId != null) {
          await updateHoliday({ id: selectedId, ...body }).unwrap();
        } else {
          await createHoliday(body).unwrap();
        }
        toast.success("Holiday saved");
      }
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save holiday");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: "Delete this entry?" });
    if (!confirmed) return;
    try {
      await deleteHoliday(selectedRecord.id).unwrap();
      toast.success("Holiday deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete holiday");
    }
  };

  const formatDate = (record) =>
    isSundayRow(record)
      ? dayjs(record.date).format("MMM YYYY")
      : dayjs(record.date).format("DD-MMM-YYYY");

  const columns = [
    { title: "Date", dataIndex: "date", render: (_, record) => formatDate(record) },
    {
      title: "Reason / Sundays",
      dataIndex: "reason",
      render: (value, record) => (isSundayRow(record) ? `${value} Sundays in the month` : value),
    },
    {
      title: "Type",
      dataIndex: "isSunday",
      render: (value) => (
        <span className={`badge ${Number(value) === 1 ? "text-bg-warning" : "badge-linesuccess"}`}>
          {Number(value) === 1 ? "Sunday" : "Holiday"}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.id)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Holidays</h4>
              <h6>Holiday calendar and recurring Sunday off-days</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing holidays requires Holidays.Create/.Edit and an
            all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-2 mb-3">
                <label className="form-label">Id</label>
                <input
                  type="text"
                  className="form-control"
                  value={mode === "new" ? "Auto-assigned" : selectedId ?? ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4 mb-3">
                <fieldset disabled={isReadOnly || !canWrite}>
                  <div className="d-flex gap-4 mt-4">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="type-holiday"
                        name="holidayType"
                        checked={type === "holiday"}
                        onChange={() => {
                          setType("holiday");
                          setDate("");
                        }}
                      />
                      <label className="form-check-label" htmlFor="type-holiday">Holiday</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="type-sunday"
                        name="holidayType"
                        checked={type === "sunday"}
                        onChange={() => {
                          setType("sunday");
                          setDate("");
                        }}
                      />
                      <label className="form-check-label" htmlFor="type-sunday">Sundays</label>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div className="col-md-3 mb-3">
                {/* Sunday entries are per month (one count row); holidays are
                    one specific date. */}
                <label className="form-label">{isMonthPicker ? "Month" : "Date"}</label>
                <input
                  type={isMonthPicker ? "month" : "date"}
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              {isMonthPicker ? (
                <div className="col-md-3 mb-3">
                  <label className="form-label">Sundays in month</label>
                  <input type="text" className="form-control" value={date ? sundayCount : ""} readOnly disabled />
                  <small className="text-muted">Counted from the month - saved as one row for the month.</small>
                </div>
              ) : (
                <div className="col-md-12 mb-3">
                  <label className="form-label">Reason</label>
                  <input
                    type="text"
                    className="form-control"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    readOnly={isReadOnly || !canWrite}
                    disabled={isReadOnly || !canWrite}
                  />
                </div>
              )}
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={records} rowKey="id" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidaysPage;
